// Lightweight password strength heuristic. Not a security check — the backend
// enforces the real minimum (`min(8)` in auth.schemas.ts). This is purely a
// UX nudge.
//
// Score range: 0 (empty) → 4 (Strong).
export function passwordScore(pw: string): 0 | 1 | 2 | 3 | 4 {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 8) s += 1;
  if (pw.length >= 12) s += 1;
  const hasLetter = /[a-zA-Z]/.test(pw);
  const hasDigit = /\d/.test(pw);
  const hasSymbol = /[^a-zA-Z0-9]/.test(pw);
  if (hasLetter && hasDigit) s += 1;
  if (hasSymbol) s += 1;
  return Math.min(4, s) as 0 | 1 | 2 | 3 | 4;
}

type PasswordScoreLabelKey = 'auth.pwWeak' | 'auth.pwFair' | 'auth.pwGood' | 'auth.pwStrong';
type PasswordPolicyKey = 'auth.pwPolicy10' | 'auth.pwPolicyMix' | 'auth.pwPolicySymbol';

// Returns an i18n key. Callers resolve with t() so the label is translated.
export function passwordScoreLabelKey(score: number): PasswordScoreLabelKey {
  if (score < 2) return 'auth.pwWeak';
  if (score < 3) return 'auth.pwFair';
  if (score < 4) return 'auth.pwGood';
  return 'auth.pwStrong';
}

export interface PasswordPolicyCheck {
  ok: boolean;
  /** i18n key — caller resolves with t() */
  labelKey: PasswordPolicyKey;
}

export function passwordPolicy(pw: string): PasswordPolicyCheck[] {
  return [
    { ok: pw.length >= 10, labelKey: 'auth.pwPolicy10' },
    { ok: /[a-zA-Z]/.test(pw) && /\d/.test(pw), labelKey: 'auth.pwPolicyMix' },
    { ok: /[^a-zA-Z0-9]/.test(pw), labelKey: 'auth.pwPolicySymbol' },
  ];
}
